import { useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { MenuSurface, MenuSurfaceItem, VISUALLY_HIDDEN, guardedLeave, hasUnsavedChanges } from "../ui";
import { useNextT } from "../i18n";
import { LegacyContactUsModal, LegacyTutorialsModal, useBillingState } from "../bridges/frame";
import { useAccess as useNextAccess } from "../access/api";
import { helpMode } from "../help/helpMode";
import { useHelpDot } from "../help/useHelp";
import { HELP_PATHS } from "../help/content";
import { Icon } from "./icons";
import { useNav } from "./useNav";
import s from "./SideFooter.module.scss";
import h from "../help/help.module.scss";

/**
 * The Help entry in the menu column. Once the Help page is served (switchboard `/help`) it is a link to that page, with a quiet dot
 * (and the spoken words "New in Help") while something new is unopened. Until then it is today's small menu with Contact us and,
 * with its permission, Tutorials (the old pop-up). onChoose: see SideMenu.
 */
export const HelpMenu = ({ onChoose }: { onChoose?(go: () => void): void }) => {
  // A locked account: only its Owner can write to us. Anyone else sees no Help and no Contact us (they see the Owner's details instead).
  const { locked } = useBillingState();
  const owner = useNextAccess().data?.role === "owner";
  if (locked && !owner) return null;
  return helpMode() === "next" ? <HelpLink onChoose={onChoose} /> : <LegacyHelpMenu onChoose={onChoose} />;
};

const HelpLink = ({ onChoose }: { onChoose?(go: () => void): void }) => {
  const { t } = useNextT();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const dot = useHelpDot(true);
  const here = /^\/(next\/)?help(\/|$)/.test(pathname);
  return (
    <Link to={HELP_PATHS.home} className={s.item} aria-current={here ? "page" : undefined}
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return; // let "open in new tab" work
        if (!onChoose && !hasUnsavedChanges()) return;
        e.preventDefault();
        const go = () => guardedLeave(() => navigate(HELP_PATHS.home));
        if (onChoose) onChoose(go); else go();
      }}>
      <Icon name="help" /><span>{t("shell.nav.help")}</span>
      {dot && <><span className={h.dot} aria-hidden="true" /><span className={VISUALLY_HIDDEN}>{t("help.dot_word")}</span></>}
    </Link>
  );
};

const LegacyHelpMenu = ({ onChoose }: { onChoose?(go: () => void): void }) => {
  const { t } = useNextT();
  const { tutorials } = useNav();
  const btn = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState<"contact" | "tutorials" | null>(null);
  const pick = (m: "contact" | "tutorials") => { setOpen(false); const go = () => setModal(m); if (onChoose) onChoose(go); else go(); };

  return (
    <>
      <button ref={btn} type="button" className={s.item} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(true)}>
        <Icon name="help" /><span>{t("shell.nav.help")}</span>
      </button>
      <MenuSurface anchorEl={btn.current} open={open} onClose={() => setOpen(false)} label={t("shell.nav.help")} placement="above-start">
        <MenuSurfaceItem icon={<Icon name="help" />} onClick={() => pick("contact")}>{t("shell.top.contact_us")}</MenuSurfaceItem>
        {tutorials && <MenuSurfaceItem icon={<Icon name="tutorials" />} onClick={() => pick("tutorials")}>{t("shell.nav.tutorials")}</MenuSurfaceItem>}
      </MenuSurface>
      <LegacyContactUsModal open={modal === "contact"} onClose={() => setModal(null)} />
      <LegacyTutorialsModal open={modal === "tutorials"} onClose={() => setModal(null)} />
    </>
  );
};
